/**
 * Routes a tile to its body renderer. S2 shipped the note tile; S3 adds the
 * chart tile (via useTileResult — the single data path). Stat/table (S4) and
 * pivot (S5) bodies still show a quiet stub until their slices land.
 *
 * The map tile is the ONE lazy body: `MapTileView` mounts the shared spatial
 * results map, so it loads via `React.lazy` to keep the spatial surfaces out
 * of the eager dashboards graph (Docs/Spatial.md bundle-isolation gate —
 * maplibre itself is a second lazy hop inside `SpatialMapLazy`).
 */
import { lazy, Suspense } from 'react';
import { BarChart3 } from 'lucide-react';
import type { DashboardTile } from '../../types/dashboardTypes';
import { ChartTileView } from './ChartTileView';
import { NoteTileView } from './NoteTileView';
import { PivotTileView } from './PivotTileView';
import { StatTileView } from './StatTileView';
import { TableTileView } from './TableTileView';

const MapTileView = lazy(() =>
  import('./MapTileView').then((m) => ({ default: m.MapTileView })),
);

/** Same geometry as QueryTileStates' skeleton — the chunk load is brief. */
function MapTileLoading() {
  return (
    <div className="flex h-full flex-col gap-2 p-3" data-testid="tile-skeleton" aria-busy="true">
      <div className="h-3 w-1/3 animate-pulse rounded bg-muted" />
      <div className="flex-1 animate-pulse rounded bg-muted/60" />
    </div>
  );
}

export function TileContent({ dashboardId, tile }: { dashboardId: string; tile: DashboardTile }) {
  if (tile.tileType === 'note') return <NoteTileView dashboardId={dashboardId} tile={tile} />;
  if (tile.tileType === 'chart') return <ChartTileView dashboardId={dashboardId} tile={tile} />;
  if (tile.tileType === 'stat') return <StatTileView dashboardId={dashboardId} tile={tile} />;
  if (tile.tileType === 'table') return <TableTileView dashboardId={dashboardId} tile={tile} />;
  if (tile.tileType === 'pivot') return <PivotTileView dashboardId={dashboardId} tile={tile} />;
  if (tile.tileType === 'map') {
    return (
      <Suspense fallback={<MapTileLoading />}>
        <MapTileView dashboardId={dashboardId} tile={tile} />
      </Suspense>
    );
  }
  // Every known tile type is routed above — this only guards future/unknown
  // tileType drift (e.g. a definition written by a newer app version).
  const unknownTile = tile as DashboardTile;
  return (
    <div
      className="flex h-full flex-col items-center justify-center gap-1.5 px-3 py-2 text-muted-foreground"
      data-testid="tile-content-stub"
    >
      <BarChart3 className="h-5 w-5 opacity-50" aria-hidden />
      <span className="text-xs italic">
        {unknownTile.tileType} tiles can&apos;t be rendered yet in this version
      </span>
    </div>
  );
}
